import type {
  EmailAliasRecovery,
  EmailAliasRecoveryFailureKind,
} from '../../../../services/api/emailAliases';

/**
 * MDF-127 - turn the recovery counts into plain sentences.
 *
 * Reporting is the reason automatic recovery is safe to operate. A silent
 * background write that did nothing looks exactly like one that did the wrong
 * thing, so this never says "done" without saying what happened.
 *
 * Two rules keep it honest, and both came out of review:
 *
 * 1. Never claim nothing was waiting unless the whole check ran. Zero also
 *    means the check failed, and "No past responses were waiting" is a
 *    positive claim about data nothing looked at. An admin who reads it stops
 *    hunting for the missing survey.
 * 2. Never tell somebody to try again when trying again cannot work. A cohort
 *    above the pass cap is skipped on every pass, because the cohort list is
 *    ordered by age.
 *
 * It lives in its own file because it is not a component. Exporting it from
 * the component file breaks fast refresh.
 */

function countOf(
  recovery: EmailAliasRecovery,
  kind: EmailAliasRecoveryFailureKind
): number {
  return recovery.failures.filter(failure => failure.kind === kind).length;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function describeAliasRecovery(recovery: EmailAliasRecovery): {
  message: string;
  color: 'green' | 'yellow';
} {
  // Responses, not rows. relinked_webhook_events is deliberately NOT added
  // here. It counts a stored webhook record that was joined to a response we
  // already held, so it recovers no answer. Paperform can also deliver two
  // events for one submission, where one creates the response and the other
  // links to it, and adding both would report one response as two.
  const recovered = recovery.replayed_webhook_events + recovery.backfilled_submissions;

  // Any failure at all means part of the search did not happen, so the count
  // above is a floor and never a complete answer.
  const searchWasComplete = recovery.failures.length === 0;

  const parts: string[] = [];

  if (recovered > 0) {
    parts.push(`Recovered ${plural(recovered, 'past response', 'past responses')}.`);
  } else if (searchWasComplete) {
    parts.push('No past responses were waiting for that address.');
  } else {
    parts.push('The address is saved. We could not finish looking for past responses.');
  }

  if (recovery.relinked_webhook_events > 0) {
    parts.push(
      `${plural(
        recovery.relinked_webhook_events,
        'stored record was',
        'stored records were'
      )} joined to a response we already held.`
    );
  }

  if (recovery.replay_capped) {
    parts.push(
      'More responses carry that address than one pass can handle. Save it again to continue.'
    );
  }

  // Each failure kind gets its own sentence, because each one asks the admin
  // to do something different.
  if (countOf(recovery, 'recovery_failed') > 0) {
    parts.push('The recovery did not run at all. Save the address again to retry it.');
  }

  if (countOf(recovery, 'replay_failed') > 0) {
    // This one reads webhook records and touches no cohort. Calling it a
    // cohort failure sent admins to look at cohorts, where nothing was wrong.
    parts.push('Stored responses could not be replayed. Save the address again to retry them.');
  }

  const held = countOf(recovery, 'replay_held');
  if (held > 0) {
    // Without this sentence a re-save inside the hold window returns all zeros
    // and no visible reason, which reads as "nothing was waiting".
    parts.push(
      'Another run is still holding some stored responses. Save the address again in a few minutes to pick them up.'
    );
  }

  const cohortFailed = countOf(recovery, 'cohort_failed');
  if (cohortFailed > 0) {
    parts.push(
      `${plural(cohortFailed, 'cohort', 'cohorts')} could not be re-imported from Paperform. ` +
        `Save the address again to retry ${cohortFailed === 1 ? 'it' : 'them'}.`
    );
  }

  const overCap = countOf(recovery, 'cohort_over_cap');
  if (overCap > 0) {
    // Not retryable. Saying "try again later" here is a dead end.
    parts.push(
      `This person is in ${plural(overCap, 'cohort', 'cohorts')} beyond what one pass checks. ` +
        `Ask an engineer to run the backfill for ${overCap === 1 ? 'that one' : 'those'}.`
    );
  }

  return {
    message: parts.join(' '),
    color: recovery.failures.length > 0 || recovery.replay_capped ? 'yellow' : 'green',
  };
}
